/**
 * Display stage for a row in the Activation Bonus table.
 *
 * `empty` has no equivalent in `MerchantLifecycle` — the backend reports it for
 * a merchant with no `activation_bonus` track at all, to separate "never
 * entered the track" from `not_started`, which means the track exists and is
 * waiting.
 */
export type ActivationBonusStage =
  "empty" | "not_started" | "eligible" | "paid" | "expired" | "failed";

export type ActivationBonusPayoutStatus = "pending" | "succeeded" | "failed";

export type CreatedRangePreset = "all" | "7d" | "30d" | "90d" | "custom";

/** Inclusive `from`, inclusive `to`, both as `YYYY-MM-DD` local calendar days. */
export interface CustomCreatedRange {
  from: string | null;
  to: string | null;
}

export type CreatedRangeFilter =
  | { preset: Exclude<CreatedRangePreset, "custom"> }
  | { preset: "custom"; range: CustomCreatedRange };

export const DEFAULT_CREATED_RANGE: CreatedRangeFilter = { preset: "all" };

/**
 * Resolves a range filter to the bounds the query takes: `createdAfter` is
 * inclusive, `createdBefore` exclusive. A custom `to` is advanced to the
 * following midnight so the named day is fully included. Both bounds are
 * anchored to the admin's local midnight, not UTC.
 */
export function resolveCreatedRange(filter: CreatedRangeFilter): {
  createdAfter: string | null;
  createdBefore: string | null;
} {
  if (filter.preset === "all")
    return { createdAfter: null, createdBefore: null };

  if (filter.preset === "custom") {
    return {
      createdAfter: startOfLocalDay(filter.range.from, 0),
      createdBefore: startOfLocalDay(filter.range.to, 1),
    };
  }

  const days = filter.preset === "7d" ? 7 : filter.preset === "30d" ? 30 : 90;
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { createdAfter: from.toISOString(), createdBefore: null };
}

/**
 * True when a custom range starts on or after it ends, which can never match a
 * row. Worth detecting rather than sending: the query has no cheap index for
 * the account-created window, so contradictory bounds cost a full scan to
 * return nothing.
 */
export function isCreatedRangeInvalid(filter: CreatedRangeFilter): boolean {
  if (filter.preset !== "custom") return false;
  const { createdAfter, createdBefore } = resolveCreatedRange(filter);
  if (!createdAfter || !createdBefore) return false;
  return Date.parse(createdAfter) >= Date.parse(createdBefore);
}

/**
 * Converts a `YYYY-MM-DD` input value to an ISO instant at local midnight,
 * offset by `dayOffset` days. Returns null for an empty or unparseable value.
 */
function startOfLocalDay(day: string | null, dayOffset: number): string | null {
  if (!day) return null;
  const parsed = new Date(`${day}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setDate(parsed.getDate() + dayOffset);
  return parsed.toISOString();
}

export interface ActivationBonusCampaignRow {
  businessId: string;
  businessName: string | null;
  email: string | null;
  /** Null when the backend reported a stage this client doesn't recognize. */
  activationBonusStage: ActivationBonusStage | null;
  accountCreatedAt: string | null;
  startedAt: string | null;
  qualifyingTransactionId: string | null;
  payoutStatus: ActivationBonusPayoutStatus | null;
  attemptCount: number;
  lastErrorMessage: string | null;
}

export type RetryPayoutStatus =
  "succeeded" | "still_failed" | "cap_reached" | "ineligible" | "error";

export interface RetryActivationBonusPayoutResult {
  status: RetryPayoutStatus;
  message?: string | null;
}
